<template>
  <a-modal
    v-model:visible="props.visible"
    title="修改头像"
    :width="660"
    :footer="null"
    @cancel="handleCancel"
    destroyOnClose
  >
    <div class="avatar-edit-container">
      <!-- 左侧裁剪区域 -->
      <div class="crop-area">
        <div class="crop-preview" v-if="imageUrl">
          <img :src="imageUrl" :style="cropImgStyle" alt="预览图" ref="cropImageRef" />
          <div class="crop-box" :style="cropBoxStyle"></div>
          <div class="size-label">200 × 200</div>
        </div>
        <div class="no-image" v-else>
          <div class="upload-placeholder">点击上传图片</div>
          <input type="file" ref="fileInputRef" @change="handleFileChange" accept="image/*" />
        </div>
      </div>
      
      <!-- 右侧效果预览 -->
      <div class="result-preview">
        <div class="circle-preview" v-if="imageUrl">
          <div class="circle-mask">
            <img :src="imageUrl" :style="circleImgStyle" alt="圆形预览" />
          </div>
        </div>
        <div class="no-image-circle" v-else>
          <div class="placeholder-circle">暂无头像</div>
        </div>
      </div>
    </div>
    
    <!-- 操作按钮区域 -->
    <div class="action-buttons">
      <div class="edit-buttons">
        <a-button @click="triggerFileInput" class="select-btn">选择 ↑</a-button>
        <a-button @click="zoomIn" :disabled="!imageUrl" class="action-btn">+</a-button>
        <a-button @click="zoomOut" :disabled="!imageUrl" class="action-btn">−</a-button>
        <a-button @click="rotateLeft" :disabled="!imageUrl" class="action-btn">
          <RotateLeftOutlined />
        </a-button>
        <a-button @click="rotateRight" :disabled="!imageUrl" class="action-btn">
          <RotateRightOutlined />
        </a-button>
      </div>
      <div class="submit-section">
        <a-button type="primary" @click="handleSubmit" :disabled="!imageUrl">
          提交
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, defineProps, defineEmits, computed, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import { RotateLeftOutlined, RotateRightOutlined } from '@ant-design/icons-vue';

// 定义属性
const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  initialAvatar: {
    type: String,
    default: ''
  }
});

// 定义事件
const emit = defineEmits(['cancel', 'submit']);

// 引用
const fileInputRef = ref(null);
const cropImageRef = ref(null);

// 状态管理
const imageUrl = ref(props.initialAvatar || '');
const scale = ref(1);
const rotation = ref(0);
const cropPosition = ref({ x: 0, y: 0 });
const cropSize = 200;

// 计算属性
const cropImgStyle = computed(() => ({
  transform: `scale(${scale.value}) rotate(${rotation.value}deg)`,
  position: 'absolute',
  left: `${cropPosition.value.x}px`,
  top: `${cropPosition.value.y}px`,
  maxWidth: 'none',
  cursor: 'move'
}));

const cropBoxStyle = computed(() => ({
  position: 'absolute',
  width: `${cropSize}px`,
  height: `${cropSize}px`,
  border: '2px solid #1890ff',
  borderRadius: '2px',
  pointerEvents: 'none'
}));

const circleImgStyle = computed(() => ({
  transform: `scale(${scale.value}) rotate(${rotation.value}deg)`,
  position: 'absolute',
  left: `-${cropPosition.value.x}px`,
  top: `-${cropPosition.value.y}px`,
  maxWidth: 'none'
}));

// 方法
const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (file) {
    // 检查文件类型
    if (!file.type.match('image.*')) {
      message.error('请选择图片文件');
      return;
    }
    
    // 检查文件大小（限制2MB）
    if (file.size > 2 * 1024 * 1024) {
      message.error('图片大小不能超过2MB');
      return;
    }
    
    // 读取文件并显示预览
    const reader = new FileReader();
    reader.onload = (event) => {
      imageUrl.value = event.target.result;
      // 重置变换参数
      scale.value = 1;
      rotation.value = 0;
      cropPosition.value = { x: 0, y: 0 };
    };
    reader.readAsDataURL(file);
  }
};

const zoomIn = () => {
  if (scale.value < 3) {
    scale.value = Math.min(scale.value + 0.1, 3);
  }
};

const zoomOut = () => {
  if (scale.value > 0.5) {
    scale.value = Math.max(scale.value - 0.1, 0.5);
  }
};

const rotateLeft = () => {
  rotation.value = (rotation.value - 90) % 360;
};

const rotateRight = () => {
  rotation.value = (rotation.value + 90) % 360;
};

const handleCancel = () => {
  // 重置状态
  imageUrl.value = props.initialAvatar || '';
  scale.value = 1;
  rotation.value = 0;
  cropPosition.value = { x: 0, y: 0 };
  
  // 清空文件输入
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
  
  emit('cancel');
};

const handleSubmit = async () => {
  try {
    if (!imageUrl.value) {
      message.error('请先选择图片');
      return;
    }
    
    // 创建canvas进行裁剪
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = cropSize;
    canvas.height = cropSize;
    
    const img = new Image();
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imageUrl.value;
    });
    
    // 保存当前上下文状态
    ctx.save();
    
    // 将原点移到canvas中心
    ctx.translate(cropSize / 2, cropSize / 2);
    
    // 应用旋转
    ctx.rotate((rotation.value * Math.PI) / 180);
    
    // 计算绘制参数
    const imgWidth = img.width * scale.value;
    const imgHeight = img.height * scale.value;
    const drawX = -cropSize / 2 + cropPosition.value.x * scale.value;
    const drawY = -cropSize / 2 + cropPosition.value.y * scale.value;
    
    // 绘制并裁剪图片
    ctx.drawImage(img, drawX, drawY, imgWidth, imgHeight);
    
    // 恢复上下文状态
    ctx.restore();
    
    // 获取裁剪后的图片数据
    const croppedImageData = canvas.toDataURL('image/png');
    
    // 触发提交事件，传递裁剪后的图片数据
    emit('submit', croppedImageData);
    
    // 关闭弹框
    handleCancel();
    
  } catch (error) {
    console.error('头像处理失败:', error);
    message.error('头像处理失败，请重试');
  }
};

// 监听props变化，更新初始头像
onMounted(() => {
  if (props.initialAvatar) {
    imageUrl.value = props.initialAvatar;
  }
});
</script>

<style scoped lang="less">
.avatar-edit-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.crop-area {
  width: 400px;
  height: 400px;
  border: 1px solid #d9d9d9;
  position: relative;
  background-color: #f5f5f5;
  overflow: hidden;
  
  &:hover .no-image input {
    opacity: 1;
  }
  
  .crop-preview {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background-color: #fafafa;
    
    .size-label {
      position: absolute;
      top: 5px;
      left: 5px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      padding: 2px 5px;
      font-size: 12px;
      border-radius: 3px;
      z-index: 10;
    }
  }
  
  .no-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .upload-placeholder {
      position: absolute;
      color: #999;
      font-size: 14px;
    }
    
    input {
      opacity: 0;
      width: 100%;
      height: 100%;
      cursor: pointer;
      position: absolute;
    }
  }
}

.result-preview {
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .circle-preview {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    position: relative;
    
    .circle-mask {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      overflow: hidden;
      position: relative;
      background-color: #f5f5f5;
      
      img {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }
  
  .no-image-circle {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    
    .placeholder-circle {
      color: #999;
      font-size: 14px;
      text-align: center;
      padding: 20px;
    }
  }
}

.action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.edit-buttons {
  display: flex;
  gap: 8px;
}

.select-btn {
  background-color: #fff;
  border-color: #d9d9d9;
  color: #262626;
}

.action-btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border-color: #d9d9d9;
  color: #262626;
}

.submit-section {
  .ant-btn-primary {
    width: 80px;
  }
}

.crop-box {
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.2);
  border: 2px dashed #1890ff;
}
</style>